import React from 'react';

import { compose, composeU } from '../lib/compose';
import { withColor } from './_color/Tag_color';
import { withSizeS } from './_size/Tag_size_s';
import { withViewClear } from './_view/Tag_view_clear';
import { withViewOutline } from './_view/Tag_view_outline';
import { cnTag } from './Tag.cn';
import type { TagBaseProps } from './Tag.types';
import { TagCounter } from './TagCounter/TagCounter';

import './Tag.css';

const enhance = compose(
    composeU(withSizeS),
    composeU(withColor),
    composeU(withViewClear, withViewOutline),
);

/**
 * @deprecated Use import { Tag } from '@yandex-int/hr-components/HrTag'
 */
export const Tag = enhance((props: TagBaseProps) => {
    const {
        className,
        text,
        icon,
        leftSlot,
        rightSlot,
        rounded,
        onClick,
        checked,
        counter,
        disabled,
        maxWidth,
        title,
        innerRef,
        size = 'm',
        ...spanProps
    } = props;

    React.useEffect(() => {
        if (process.env.NODE_ENV === 'development')
            console.warn('[Tag]: Эта версия компонента устарела и скоро будет удалена');
    }, []);

    const onClickHandler = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (onClick && !disabled) {
                onClick(event);
            }
        },
        [onClick, disabled],
    );

    const handleKeypress = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            //it triggers by pressing the enter key
            if (event.code === 'Enter' && onClick && !disabled) {
                onClick(event);
            }
        },
        [onClick, disabled],
    );

    const onClickStopPropagation = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    }, []);

    // Нет ни текста, ни иконки, рисовать нечего
    if (!text && !icon) {
        return null;
    }

    const interactive = Boolean(onClick);

    const onlyIcon = Boolean(!text && icon);

    const roundeValue = rounded ? rounded : Boolean(leftSlot);

    const classNameValue = cnTag(
        { size, rounded: roundeValue, checked, interactive, onlyIcon, disabled },
        [className],
    );

    // Нет текста, только иконка
    if (!text && icon) {
        return (
            <span {...spanProps} ref={innerRef} onClick={onClickHandler} className={classNameValue}>
                {icon}
            </span>
        );
    }
    const leftSlotRender = leftSlot ? <span className={cnTag('LeftSlot')}>{leftSlot}</span> : null;

    const rightSlotRender =
        rightSlot && !disabled ? (
            <span onClick={onClickStopPropagation} className={cnTag('RightSlot')}>
                {rightSlot}
            </span>
        ) : null;

    const counterRender = counter ? <TagCounter text={counter} /> : null;

    const maxWidthValue = maxWidth === 'full' ? '100%' : maxWidth;
    const widthSetting = maxWidth
        ? {
              maxWidth: maxWidthValue,
          }
        : {};

    if (text) {
        return (
            <span
                {...spanProps}
                ref={innerRef}
                onClick={onClickHandler}
                onKeyDown={handleKeypress}
                className={classNameValue}
                title={title}
                tabIndex={interactive ? 0 : -1}
            >
                {leftSlotRender}
                <span style={widthSetting} className={cnTag('Text')}>
                    {text}
                </span>
                {counterRender}
                {rightSlotRender}
            </span>
        );
    }

    return null;
});

Tag.displayName = cnTag();

export type TagProps = React.ComponentProps<typeof Tag>;
